import { useMutation, useQueryClient } from '@tanstack/react-query';

import { message } from 'antd';
import { AxiosError } from 'axios';

import { Namespace, TFunction, useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { UpdateOrderParamsDTO } from '@app/models/orders';
import Api from '@app/services/api';
import { getFormErrorsFromResponse } from '@app/services/getFormErrorsFromResponse';

interface IParams {
  order_uid: string;
  data: UpdateOrderParamsDTO;
}

interface IProductLimitErrorMeta {
  product_title?: string;
  requested_quantity?: number;
  available_quantity?: number;
  consumed_quantity?: number;
}

interface IProductLimitErrorResponse {
  errors?: Array<{
    code?: string;
    meta?: IProductLimitErrorMeta | null;
  }>;
}

interface IInsufficientStockItem {
  code?: string | null;
  requested?: number;
  max_quantity?: number;
}

interface IInsufficientStockErrorResponse {
  errors?: Array<{
    code?: string;
    meta?: { items?: IInsufficientStockItem[] } | null;
  }>;
}

const getProductLimitErrorMeta = (error: unknown): Required<IProductLimitErrorMeta> | null => {
  const productLimitError = (error as AxiosError<IProductLimitErrorResponse>).response?.data?.errors?.find(
    (item) => item.code === 'order_product_limit_exceeded'
  );
  const meta = productLimitError?.meta;

  if (
    !meta?.product_title ||
    meta.available_quantity == null ||
    meta.requested_quantity == null ||
    meta.consumed_quantity == null
  ) {
    return null;
  }

  return {
    product_title: meta.product_title,
    available_quantity: meta.available_quantity,
    requested_quantity: meta.requested_quantity,
    consumed_quantity: meta.consumed_quantity,
  };
};

const getInsufficientStockItems = (error: unknown): Required<IInsufficientStockItem>[] => {
  const stockError = (error as AxiosError<IInsufficientStockErrorResponse>).response?.data?.errors?.find(
    (item) => item.code === 'insufficient_product_stock'
  );

  return (stockError?.meta?.items || []).reduce((acc, item) => {
    if (item.requested != null && item.max_quantity != null) {
      acc.push({ code: item.code || null, requested: item.requested, max_quantity: item.max_quantity });
    }
    return acc;
  }, [] as Required<IInsufficientStockItem>[]);
};

const getInsufficientStockMessage = (
  items: Required<IInsufficientStockItem>[],
  t: TFunction<Namespace<'validation'>>
): string =>
  t('insufficient_product_stock_with_meta', {
    items: items
      .map((item) =>
        t('insufficient_product_stock_item', {
          code: item.code || '–',
          requested: item.requested,
          maxQuantity: item.max_quantity,
          interpolation: { escapeValue: false },
        })
      )
      .join('; '),
    interpolation: { escapeValue: false },
  });

export const useUpdateQuery = () => {
  const client = useQueryClient();
  const commonT = useTranslation(NSKeys.common);
  const validationT = useTranslation(NSKeys.validation);

  return useMutation({
    mutationFn: async ({ order_uid, data }: IParams) => {
      await Api.orders.updateOrder(order_uid, data);
    },
    onSuccess: async (_, { order_uid }) => {
      await client.invalidateQueries(['getOrder', order_uid]);
      await client.invalidateQueries(['getHistory', order_uid]);
      message.open({
        type: 'success',
        content: commonT.t('dataPatchSuccess'),
      });
    },
    onError: (error, data) => {
      const { formMessages } = getFormErrorsFromResponse(data, error, validationT.t);
      const productLimitMeta = getProductLimitErrorMeta(error);
      const productLimitMessage = productLimitMeta
        ? validationT.t('order_product_limit_exceeded_with_meta', {
            productTitle: productLimitMeta.product_title,
            availableQuantity: productLimitMeta.available_quantity,
            requestedQuantity: productLimitMeta.requested_quantity,
            consumedQuantity: productLimitMeta.consumed_quantity,
            interpolation: { escapeValue: false },
          })
        : null;

      const insufficientStockItems = getInsufficientStockItems(error);
      const insufficientStockMessage = insufficientStockItems.length
        ? getInsufficientStockMessage(insufficientStockItems, validationT.t)
        : null;

      message.open({
        type: 'error',
        duration: 5,
        content: productLimitMessage || insufficientStockMessage || formMessages[0] || commonT.t('error400'),
      });
    },
  });
};
